import React, { useEffect, useMemo, useRef, useState } from 'react'
import cn from 'classnames'
import PayloadContainer, {
    type PayloadContainerItem,
} from 'components/answers/Answer/PayloadContainer'
import { pickMediaFile } from 'components/quests/mintMediaThreadFlow'
import { isMobile } from 'lib/device'
import styles from './new-music-modal.module.scss'

export const MUSIC_TITLE_MAX_LENGTH = 200

export type MusicKind = 'audio' | 'video'

export type MusicForm = {
    title: string
    description: string
    cover: File | null
}

// The New Music modal: the song previewed the way the posted thread will show
// it (a cover and its audio fused into one combined-media tile, or the video
// player), then title, description and, for audio, a cover. The body scrolls
// and Post stays pinned under it, so adding a cover never pushes Post off
// screen; on a phone the card stays inside the safe areas and above the
// keyboard.
export default function NewMusicModal({
    file,
    kind,
    initial,
    onSubmit,
    onCancel,
    closeModal,
}: {
    file: File
    kind: MusicKind
    initial: MusicForm
    onSubmit: (form: MusicForm) => void
    onCancel: () => void
    closeModal: () => void
}) {
    const [title, setTitle] = useState(initial.title)
    const [description, setDescription] = useState(initial.description)
    const [cover, setCover] = useState<File | null>(initial.cover)
    const submitted = useRef(false)
    const visualViewportHeight = useVisualViewportHeight()

    const mediaUrl = useMemo(() => URL.createObjectURL(file), [file])
    const coverUrl = useMemo(() => (cover ? URL.createObjectURL(cover) : null), [cover])
    useEffect(() => () => URL.revokeObjectURL(mediaUrl), [mediaUrl])
    useEffect(() => () => coverUrl ? URL.revokeObjectURL(coverUrl) : undefined, [coverUrl])

    // Closing by X, overlay or Escape unmounts the modal without a Post.
    const cancel = useRef(onCancel)
    cancel.current = onCancel
    useEffect(
        () => () => {
            if (!submitted.current) cancel.current()
        },
        [],
    )

    const items: PayloadContainerItem[] = [
        ...(coverUrl
            ? [
                  {
                      id: 'new-music-cover',
                      label: 'Cover',
                      status: 'succeeded' as const,
                      kind: 'image' as const,
                      url: coverUrl,
                      contentType: cover?.type || 'image/jpeg',
                      mediaIdentityKey: `new-music-cover:${coverUrl}`,
                  },
              ]
            : []),
        {
            id: 'new-music-media',
            label: title || file.name,
            status: 'succeeded',
            kind,
            url: mediaUrl,
            contentType: file.type,
            mediaIdentityKey: `new-music-media:${mediaUrl}`,
        },
    ]

    const pickCover = async () => {
        const picked = await pickMediaFile('image/*')
        if (!picked || !picked.type.toLowerCase().startsWith('image/')) return
        setCover(picked)
    }

    const post = () => {
        submitted.current = true
        onSubmit({ title, description, cover })
        closeModal()
    }

    return (
        <div
            className={cn(styles.modal, isMobileSurface() && styles.mobile)}
            style={
                visualViewportHeight
                    ? ({ '--new-music-viewport-height': `${visualViewportHeight}px` } as any)
                    : undefined
            }
            data-testid="new-music-form"
        >
            <div className={styles.header}>
                <h2 className={styles.title}>New Music</h2>
            </div>

            <div className={styles.body}>
                <div className={styles.preview}>
                    <PayloadContainer hideSingleModelLabel items={items} />
                </div>

                {kind === 'audio' && (
                    <div className={styles.coverRow}>
                        <button
                            type="button"
                            className={styles.coverButton}
                            data-testid="new-music-cover-button"
                            onClick={pickCover}
                        >
                            {cover ? 'Change cover' : 'Add cover'}
                        </button>
                        {cover && (
                            <button
                                type="button"
                                className={styles.coverRemove}
                                onClick={() => setCover(null)}
                            >
                                Remove
                            </button>
                        )}
                    </div>
                )}

                <label className={styles.field}>
                    <span>Title</span>
                    <input
                        type="text"
                        data-testid="new-music-title-input"
                        maxLength={MUSIC_TITLE_MAX_LENGTH}
                        value={title}
                        onChange={event => setTitle(event.target.value)}
                    />
                </label>

                <label className={styles.field}>
                    <span>Description</span>
                    <textarea
                        data-testid="new-music-description-input"
                        rows={3}
                        value={description}
                        onChange={event => setDescription(event.target.value)}
                    />
                </label>
            </div>

            <div className={styles.footer}>
                <button
                    type="button"
                    className={styles.postButton}
                    data-testid="new-music-post-button"
                    onClick={post}
                >
                    Post
                </button>
            </div>
        </div>
    )
}

function isMobileSurface(): boolean {
    return (
        isMobile || (typeof window !== 'undefined' && Boolean((window as any).KNOVIGATOR_IS_MOBILE))
    )
}

// The visible height on a phone, which shrinks while the keyboard is open.
function useVisualViewportHeight(): number | null {
    const [height, setHeight] = useState<number | null>(null)
    useEffect(() => {
        const viewport = window.visualViewport
        if (!isMobileSurface() || !viewport) return
        const update = () => setHeight(viewport.height)
        update()
        viewport.addEventListener('resize', update)
        return () => viewport.removeEventListener('resize', update)
    }, [])
    return height
}
